import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TemplateForm, LoadingView } from './ContractModal';

// "Generate Assignment" — mirrors the purchase-contract flow. The first screen shows the org's
// Backside Assignment template with ALL mapped fields (property metrics auto-filled from the deal,
// Variable/Pre-filled fields editable) — exactly like generating a purchase contract. On submit it
// fills the template (keeping it fillable) and shows the PDF inline, every field still editable.
// Optionally link an end buyer for KPIs/record-keeping (the purchasing entity name is typed on the
// contract itself, since buyers usually take title in an LLC).
export default function GenerateAssignmentModal({ deal, onClose }) {
  const [tplMode, setTplMode]   = useState('loading');   // 'loading' | 'template' | 'no-template'
  const [template, setTemplate] = useState(null);
  const [tplFields, setTplFields] = useState([]);
  const [tplRow, setTplRow]     = useState(null);

  const [buyers, setBuyers] = useState([]);
  const [q, setQ] = useState('');
  const [buyerId, setBuyerId] = useState(deal?.assignee_buyer_id || null);

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);       // { filename, assignee }
  const navigate = useNavigate();

  const isMlsDeal = !!(deal && deal.mls_id);

  // Load the Backside Assignment template + its mapped fields (same as ContractModal for purchase).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetch('/api/contracts/templates', { credentials: 'include' }).then(r => r.json());
        const row = Array.isArray(list) ? list.find(t => t.slot === 'backside_assignment') : null;
        if (cancelled) return;
        setTplRow(row || null);
        if (row && row.exists && row.status === 'ready' && row.id) {
          const fx = await fetch(`/api/contracts/templates/${row.id}/fields`, { credentials: 'include' }).then(r => r.json());
          if (cancelled) return;
          setTemplate({ id: row.id, name: row.name, status: row.status });
          setTplFields(fx.fields || []);
          setTplMode('template');
        } else if (!cancelled) {
          setTplMode('no-template');
        }
      } catch { if (!cancelled) setTplMode('no-template'); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetch('/api/buyers?limit=5300').then(r => r.ok ? r.json() : []).then(d => {
      setBuyers(Array.isArray(d) ? d : (Array.isArray(d?.buyers) ? d.buyers : []));
    }).catch(() => {});
  }, []);

  const filtered = q
    ? buyers.filter(b => `${b.name || ''} ${b.company || ''} ${b.email || ''}`.toLowerCase().includes(q.toLowerCase()))
    : buyers;

  async function handleSubmit(fieldValues) {
    setBusy(true);
    try {
      const body = { deal_id: deal.id, field_values: fieldValues };
      if (buyerId) body.assignee_buyer_id = buyerId;
      const r = await fetch('/api/contracts/generate-assignment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.status === 409 && d.needs_template) { setTplMode('no-template'); setBusy(false); return; }
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      setDone({ filename: d.filename, assignee: d.assignee });
    } catch (e) { alert(`Failed: ${e.message}`); }
    setBusy(false);
  }

  const wide = tplMode === 'template' || !!done;
  return (
    <div style={overlay} onClick={onClose}>
      <div style={wide ? panelWide : panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Generate Assignment</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{deal?.address || ''} · fills your Backside Assignment template</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {done ? (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#4A7C59', marginBottom: 3 }}>✓ Assignment ready{done.assignee?.name ? ` — ${done.assignee.name} linked to this deal` : ''}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>Every field below is editable — fill any remaining blanks, then save/print from the viewer, or download.</div>
              <iframe title="Assignment contract" src={`/api/contracts/download/${encodeURIComponent(done.filename)}?inline=1`}
                style={{ width: '100%', height: '62vh', border: '1px solid var(--navy-border)', borderRadius: 8, background: '#fff' }} />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                <a href={`/api/contracts/download/${encodeURIComponent(done.filename)}`} target="_blank" rel="noreferrer"
                  style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-block' }}>Download PDF</a>
                <button onClick={onClose} style={primaryBtn}>Done</button>
              </div>
            </div>
          ) : busy ? (
            <LoadingView label="Filling your assignment template…" />
          ) : tplMode === 'loading' ? (
            <LoadingView label="Loading assignment template…" />
          ) : tplMode === 'no-template' ? (
            <div>
              <div style={{ padding: 12, background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 8, color: 'var(--gold)', fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                {tplRow && tplRow.exists
                  ? 'Your Backside Assignment PDF is uploaded, but its fields aren’t mapped yet. Map them (property fields → "MLS Metric": address, legal description, APN; the rest as editable fields).'
                  : 'Set up your assignment contract first — upload your assignment PDF to the Backside Assignment slot and map its fields, same as the purchase contract.'}
              </div>
              <button onClick={() => navigate('/settings/templates/backside_assignment')} style={primaryBtn}>
                {tplRow && tplRow.exists ? 'Map fields' : 'Set up assignment template'}
              </button>
            </div>
          ) : (
            <>
              {/* Optional buyer link — for KPIs / who-took-the-deal. The entity name is typed on the contract. */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Link end buyer <span style={{ color: 'var(--gray-500)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· optional — records who took this deal (for your KPIs)</span></label>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search buyers by name, company, email…" style={{ ...inputStyle, marginBottom: 8 }} />
                <div style={{ maxHeight: 130, overflowY: 'auto', border: '1px solid var(--navy-border)', borderRadius: 8 }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: 12, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12 }}>No buyers found.</div>
                  ) : filtered.slice(0, 50).map(b => (
                    <div key={b.id} onClick={() => setBuyerId(buyerId === b.id ? null : b.id)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px', cursor: 'pointer', background: buyerId === b.id ? 'var(--navy)' : 'transparent', borderBottom: '1px solid rgba(232,227,218,0.4)' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{b.name}{b.company ? ` · ${b.company}` : ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[b.email, b.phone].filter(Boolean).join(' · ')}</div>
                      </div>
                      {buyerId === b.id && <span style={{ fontSize: 11, color: '#4A7C59', fontWeight: 700 }}>✓ linked</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* The mapped template fields — same editable form as the purchase contract. */}
              <TemplateForm deal={deal} template={template} fields={tplFields} isMlsDeal={isMlsDeal}
                submitLabel="Generate Assignment" onSubmit={handleSubmit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel     = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(560px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const panelWide = { ...panel, width: 'min(960px, 100%)' };
const closeBtn  = { background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: 0 };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 14 };
const ghostBtn   = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const primaryBtn = { padding: '8px 18px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
